import React, { useState, useEffect } from 'react';
import skyHostLogo from './assets/images/regenerated_image_1780444681023.jpg';
import { 
  UserRole,
  CompanyProfile, 
  Client, 
  ParkingSpot, 
  AccommodationType, 
  Accommodation, 
  StockCategory, 
  StockItem, 
  Minibar, 
  Reservation, 
  SystemState,
  Hotel
} from './types';
import { getInitialState, saveState } from './utils/storage';
import { 
  dbSetDoc, 
  dbDeleteDoc, 
  bootstrapDatabaseIfEmpty, 
  testConnection, 
  handleFirestoreError, 
  OperationType,
  apiRequest
} from './utils/firebase';

// Import Subcomponents
import Login from './components/Login';
import ConfigProfile from './components/ConfigProfile';
import Clients from './components/Clients';
import Parking from './components/Parking';
import Accommodations from './components/Accommodations';
import AccommodationBoard from './components/AccommodationBoard';
import Stock from './components/Stock';
import Minibars from './components/Minibars';
import Reservas from './components/Reservas';
import Hotels from './components/Hotels';
import Reports from './components/Reports';

// Import Lucide Icons
import {
  Calendar,
  Home,
  Users,
  Compass,
  Layers,
  Settings,
  LogOut,
  Sparkles,
  ToggleLeft,
  ShieldCheck,
  Package,
  Cpu,
  BookmarkCheck,
  Bed,
  CheckCircle2,
  ChevronRight,
  Menu,
  X,
  Building2,
  BookOpen,
  TrendingUp
} from 'lucide-react';

export default function App() {
  // Master persistent state
  const [state, setState] = useState<SystemState>(() => getInitialState());
  const [activeTab, setActiveTab] = useState<string>('reservas');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Synchronize master state modifications to browser local storage
  useEffect(() => {
    saveState(state);
  }, [state]);

  // Bootstrap the database with initial values if it's completely empty
  useEffect(() => {
    const initDb = async () => {
      await testConnection();
      const initialState = getInitialState();
      await bootstrapDatabaseIfEmpty(
        initialState.hotels,
        initialState.clients,
        initialState.parkingSpots,
        initialState.accommodationTypes,
        initialState.accommodations,
        initialState.stockCategories,
        initialState.stockItems,
        initialState.minibars,
        initialState.reservations,
        initialState.profile
      );
    };
    initDb();
  }, []);

  // Helper para parsear campos JSON que vêm como string do D1
  const parseJsonField = <T,>(value: any, fallback: T): T => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      try { return JSON.parse(value); } catch { return fallback; }
    }
    return value;
  };

  const normalizeAccommodation = (a: any): Accommodation => ({
    ...a,
    parkingSpots: Array.isArray(a.parkingSpots) ? a.parkingSpots : parseJsonField(a.parkingSpots, [])
  });

  const normalizeMinibar = (m: any): Minibar => ({
    ...m,
    items: Array.isArray(m.items) ? m.items : parseJsonField(m.items, [])
  });

  const normalizeReservation = (r: any): Reservation => ({
    ...r,
    consumptions: Array.isArray(r.consumptions) ? r.consumptions : parseJsonField(r.consumptions, []),
    additionalCosts: Array.isArray(r.additionalCosts) ? r.additionalCosts : parseJsonField(r.additionalCosts, []),
    checkInDetails: r.checkInDetails && typeof r.checkInDetails === 'object' ? r.checkInDetails : parseJsonField(r.checkInDetails, undefined)
  });

  // Fetch data from API on mount and periodically for cross-device sync
  useEffect(() => {
    const fetchData = async () => {
      try {
        const safeFetch = async <T,>(endpoint: string, fallback: T): Promise<T> => {
          try {
            return await apiRequest<T>(endpoint);
          } catch (e) {
            console.warn(`Failed to fetch ${endpoint}:`, e);
            return fallback;
          }
        };

        const [
          profileRes,
          hotelsRes,
          clientsRes,
          parkingRes,
          typesRes,
          accommodationsRes,
          categoriesRes,
          stockItemsRes,
          minibarsRes,
          reservationsRes
        ] = await Promise.all([
          safeFetch<CompanyProfile>('/profile', {} as CompanyProfile),
          safeFetch<Hotel[]>('/hotels', []),
          safeFetch<Client[]>('/clients', []),
          safeFetch<ParkingSpot[]>('/parking', []),
          safeFetch<AccommodationType[]>('/accommodation-types', []),
          safeFetch<Accommodation[]>('/accommodations', []),
          safeFetch<StockCategory[]>('/stock-categories', []),
          safeFetch<StockItem[]>('/stock-items', []),
          safeFetch<Minibar[]>('/minibars', []),
          safeFetch<Reservation[]>('/reservations', [])
        ]);

        setState(prev => ({
          ...prev,
          profile: profileRes || prev.profile,
          hotels: hotelsRes.length > 0 ? hotelsRes : prev.hotels,
          clients: clientsRes,
          parkingSpots: parkingRes,
          accommodationTypes: typesRes,
          accommodations: accommodationsRes.map(normalizeAccommodation),
          stockCategories: categoriesRes,
          stockItems: stockItemsRes,
          minibars: minibarsRes.map(normalizeMinibar),
          reservations: reservationsRes.map(normalizeReservation)
        }));
        console.log('[SkyHost] Data synced from API. Hotels:', hotelsRes.length);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Logout/Login actions
  const handleLogin = (role: UserRole, hotelId: string, isSuper?: boolean) => {
    setState(prev => ({
      ...prev,
      activeRole: role,
      selectedHotelId: isSuper ? (prev.selectedHotelId || prev.hotels?.[0]?.id || 'hotel_1') : hotelId,
      isLoggedIn: true
    }));
    if (isSuper) {
      setActiveTab('hoteis');
    } else {
      setActiveTab('reservas');
    }
  };

  const handleLogout = () => {
    setState(prev => ({
      ...prev,
      isLoggedIn: false
    }));
  };

  const overrideRole = (role: UserRole) => {
    setState(prev => ({
      ...prev,
      activeRole: role
    }));
    if (role === 'recepcionista' && activeTab === 'config') {
      setActiveTab('reservas');
    }
  };

  const isAdmin = state.activeRole === 'administrador' || state.activeRole === 'super';
  const isSuperAdmin = state.activeRole === 'super';

  // State modifiers
  const handleUpdateProfile = async (newProfile: CompanyProfile) => {
    const ok1 = await dbSetDoc('profile', 'default', newProfile);
    const updatedHotels = state.hotels?.map(h => {
      if (h.id === state.selectedHotelId) {
        const u = {
          ...h,
          name: newProfile.name,
          corporateName: newProfile.corporateName,
          document: newProfile.document,
          phone: newProfile.phone,
          email: newProfile.email,
          address: newProfile.address,
          pixKey: newProfile.pixKey
        };
        return u;
      }
      return h;
    }) || [];

    setState(prev => ({
      ...prev,
      profile: newProfile,
      hotels: updatedHotels
    }));

    if (ok1) {
      const target = state.hotels?.find(h => h.id === state.selectedHotelId);
      if (target) {
        const u = { ...target, ...newProfile };
        await dbSetDoc('hotels', target.id, u);
      }
      alert("Dados corporativos desta unidade gravados com sucesso!");
    } else {
      alert("Erro ao salvar dados. Tente novamente.");
    }
  };

  // HOTELS MANAGEMENT
  const handleAddHotel = async (hotel: Omit<Hotel, 'id' | 'createdAt'>) => {
    const hotelId = `hotel_${Date.now()}`;
    const newHotel: Hotel = {
      id: hotelId,
      createdAt: new Date().toISOString().split('T')[0],
      ...hotel
    };
    const ok = await dbSetDoc('hotels', hotelId, newHotel);
    if (ok) {
      setState(prev => ({
        ...prev,
        hotels: [...(prev.hotels || []), newHotel]
      }));
      alert(`Hotel/Unidade "${hotel.name}" cadastrada com sucesso!`);
    } else {
      alert("Erro ao salvar hotel na nuvem. Verifique sua conexão e tente novamente.");
    }
  };

  const handleUpdateHotel = async (id: string, updates: Partial<Hotel>) => {
    const target = state.hotels.find(h => h.id === id);
    if (target) {
      const updated = { ...target, ...updates };
      const ok = await dbSetDoc('hotels', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          hotels: (prev.hotels || []).map(h => h.id === id ? updated : h)
        }));
      } else {
        alert("Erro ao atualizar hotel na nuvem.");
      }
    }
  };

  // CLIENTS
  const handleAddClient = async (client: Omit<Client, 'id'>) => {
    const clientId = `cli_${Date.now()}`;
    const newClient: Client = {
      id: clientId,
      hotelId: state.selectedHotelId,
      ...client
    };
    const ok = await dbSetDoc('clients', clientId, newClient);
    if (ok) {
      setState(prev => ({
        ...prev,
        clients: [...prev.clients, newClient]
      }));
    } else {
      alert("Erro ao salvar hóspede na nuvem.");
    }
  };

  const handleDeleteClient = async (id: string) => {
    const ok = await dbDeleteDoc('clients', id);
    if (ok) {
      setState(prev => ({
        ...prev,
        clients: prev.clients.filter(c => c.id !== id)
      }));
    } else {
      alert("Erro ao excluir hóspede na nuvem.");
    }
  };

  // PARKING SPOTS
  const handleAddParkingSpot = async (spot: Omit<ParkingSpot, 'id'>) => {
    const spotId = `pk_${Date.now()}`;
    const newSpot: ParkingSpot = {
      id: spotId,
      hotelId: state.selectedHotelId,
      ...spot
    };
    const ok = await dbSetDoc('parkingSpots', spotId, newSpot);
    if (ok) {
      setState(prev => ({
        ...prev,
        parkingSpots: [...prev.parkingSpots, newSpot]
      }));
    } else {
      alert("Erro ao salvar vaga na nuvem.");
    }
  };

  const handleDeleteParkingSpot = async (id: string) => {
    const ok = await dbDeleteDoc('parkingSpots', id);
    if (ok) {
      setState(prev => ({
        ...prev,
        parkingSpots: prev.parkingSpots.filter(p => p.id !== id)
      }));
    } else {
      alert("Erro ao excluir vaga na nuvem.");
    }
  };

  const handleUpdateParkingSpot = async (id: string, updates: Partial<ParkingSpot>) => {
    const target = state.parkingSpots.find(p => p.id === id);
    if (target) {
      const updated = { ...target, ...updates };
      const ok = await dbSetDoc('parkingSpots', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          parkingSpots: prev.parkingSpots.map(p => p.id === id ? updated : p)
        }));
      } else {
        alert("Erro ao atualizar vaga na nuvem.");
      }
    }
  };

  // ACCOMMODATIONS
  const handleAddAccType = async (name: string) => {
    const typeId = `tp_${Date.now()}`;
    const newType: AccommodationType = {
      id: typeId,
      name,
      active: true
    };
    const ok = await dbSetDoc('accommodationTypes', typeId, newType);
    if (ok) {
      setState(prev => ({
        ...prev,
        accommodationTypes: [...prev.accommodationTypes, newType]
      }));
    } else {
      alert("Erro ao salvar tipo na nuvem.");
    }
  };

  const handleUpdateAccType = async (id: string, active: boolean) => {
    const target = state.accommodationTypes.find(t => t.id === id);
    if (target) {
      const updated = { ...target, active };
      const ok = await dbSetDoc('accommodationTypes', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          accommodationTypes: prev.accommodationTypes.map(t => t.id === id ? updated : t)
        }));
      } else {
        alert("Erro ao atualizar tipo na nuvem.");
      }
    }
  };

  // ACCOMMODATIONS
  const handleAddAccommodation = async (acc: Omit<Accommodation, 'id'>) => {
    const accId = `ac_${Date.now()}`;
    const newAcc: Accommodation = {
      id: accId,
      hotelId: state.selectedHotelId,
      ...acc
    };
    const ok = await dbSetDoc('accommodations', accId, newAcc);
    if (ok) {
      setState(prev => ({
        ...prev,
        accommodations: [...prev.accommodations, newAcc]
      }));
    } else {
      alert("Erro ao salvar acomodação na nuvem.");
    }
  };

  const handleUpdateAccommodation = async (id: string, updates: Partial<Accommodation>) => {
    const target = state.accommodations.find(a => a.id === id);
    if (target) {
      const updated = { ...target, ...updates };
      const ok = await dbSetDoc('accommodations', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          accommodations: prev.accommodations.map(a => a.id === id ? updated : a)
        }));
      } else {
        alert("Erro ao atualizar acomodação na nuvem.");
      }
    }
  };

  // STOCK
  const handleAddStockCategory = async (name: string) => {
    const catId = `sc_${Date.now()}`;
    const newCat: StockCategory = {
      id: catId,
      name,
      active: true
    };
    const ok = await dbSetDoc('stockCategories', catId, newCat);
    if (ok) {
      setState(prev => ({
        ...prev,
        stockCategories: [...prev.stockCategories, newCat]
      }));
    } else {
      alert("Erro ao salvar categoria na nuvem.");
    }
  };

  const handleUpdateStockCategory = async (id: string, active: boolean) => {
    const target = state.stockCategories.find(c => c.id === id);
    if (target) {
      const updated = { ...target, active };
      const ok = await dbSetDoc('stockCategories', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          stockCategories: state.stockCategories.map(c => c.id === id ? updated : c)
        }));
      } else {
        alert("Erro ao atualizar categoria na nuvem.");
      }
    }
  };

  const handleAddStockItem = async (item: Omit<StockItem, 'id'>) => {
    const itemId = `si_${Date.now()}`;
    const newItem: StockItem = {
      id: itemId,
      hotelId: state.selectedHotelId,
      ...item
    };
    const ok = await dbSetDoc('stockItems', itemId, newItem);
    if (ok) {
      setState(prev => ({
        ...prev,
        stockItems: [...prev.stockItems, newItem]
      }));
    } else {
      alert("Erro ao salvar item no estoque na nuvem.");
    }
  };

  const handleUpdateStockItem = async (id: string, updates: Partial<StockItem>) => {
    const target = state.stockItems.find(si => si.id === id);
    if (target) {
      const updated = { ...target, ...updates, updatedAt: new Date().toISOString(), updatedBy: state.activeRole };
      const ok = await dbSetDoc('stockItems', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          stockItems: prev.stockItems.map(si => si.id === id ? updated : si)
        }));
      } else {
        alert("Erro ao atualizar item no estoque na nuvem.");
      }
    }
  };

  // Multiple row items bulk add
  const handleAddStockItemBulk = async (itemsList: Omit<StockItem, 'id'>[]) => {
    const createdItems: StockItem[] = itemsList.map((item, idx) => ({
      id: `si_bulk_${Date.now()}_${idx}`,
      hotelId: state.selectedHotelId,
      ...item
    }));

    let allOk = true;
    for (const item of createdItems) {
      const ok = await dbSetDoc('stockItems', item.id, item);
      if (!ok) allOk = false;
    }

    if (allOk) {
      setState(prev => ({
        ...prev,
        stockItems: [...prev.stockItems, ...createdItems]
      }));
      alert(`${createdItems.length} novos itens adicionados em lote no estoque central!`);
    } else {
      alert("Alguns itens falharam ao salvar na nuvem. Verifique sua conexão.");
    }
  };

  // MINIBARS
  const handleAddMinibar = async (mb: Omit<Minibar, 'id' | 'items'>) => {
    const newId = `mb_${Date.now()}`;
    const newMb: Minibar = {
      id: newId,
      items: [],
      hotelId: state.selectedHotelId,
      ...mb
    };
    const ok = await dbSetDoc('minibars', newId, newMb);
    if (ok) {
      setState(prev => ({
        ...prev,
        minibars: [...prev.minibars, newMb]
      }));
      alert(`Frigobar patrimônio ${mb.tag} cadastrado.`);
    } else {
      alert("Erro ao salvar frigobar na nuvem.");
    }
  };

  const handleUpdateMinibar = async (id: string, updates: Partial<Minibar>) => {
    const target = state.minibars.find(m => m.id === id);
    if (target) {
      const updated = { ...target, ...updates };
      const ok = await dbSetDoc('minibars', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          minibars: prev.minibars.map(m => m.id === id ? updated : m)
        }));
      } else {
        alert("Erro ao atualizar frigobar na nuvem.");
      }
    }
  };

  // Abastecer Minibar transfer from central inventory
  const handleAddItemToMinibar = async (minibarId: string, itemId: string, qty: number): Promise<boolean> => {
    // 1. Locate stock quantity
    const stockItem = state.stockItems.find(i => i.id === itemId);
    if (!stockItem || stockItem.quantity < qty) return false;

    // 2. Reduce general stock quantity
    const updatedStockItem = { ...stockItem, quantity: stockItem.quantity - qty };

    // 3. Increment/add to minibar items
    const targetMinibar = state.minibars.find(m => m.id === minibarId);
    if (!targetMinibar) return false;

    const existingItemIdx = targetMinibar.items.findIndex(it => it.itemId === itemId);
    let updatedContainedItems = [...targetMinibar.items];

    if (existingItemIdx !== -1) {
      updatedContainedItems[existingItemIdx] = {
        ...updatedContainedItems[existingItemIdx],
        quantity: updatedContainedItems[existingItemIdx].quantity + qty
      };
    } else {
      updatedContainedItems.push({ itemId, quantity: qty });
    }

    const updatedMinibar = { ...targetMinibar, items: updatedContainedItems };

    const ok1 = await dbSetDoc('stockItems', itemId, updatedStockItem);
    const ok2 = await dbSetDoc('minibars', minibarId, updatedMinibar);

    if (ok1 && ok2) {
      const updatedStockItems = state.stockItems.map(si => si.id === itemId ? updatedStockItem : si);
      const updatedMinibars = state.minibars.map(mb => mb.id === minibarId ? updatedMinibar : mb);
      setState(prev => ({
        ...prev,
        stockItems: updatedStockItems,
        minibars: updatedMinibars
      }));
      return true;
    }
    alert("Erro ao transferir insumo para frigobar na nuvem.");
    return false;
  };

  // Retirar item do frigobar and return to standard stock
  const handleRemoveItemFromMinibar = async (minibarId: string, itemId: string, qty: number) => {
    // 1. Return/add quantity to central stock
    const stockItem = state.stockItems.find(i => i.id === itemId);
    if (!stockItem) return;
    const updatedStockItem = { ...stockItem, quantity: stockItem.quantity + qty };

    // 2. Reduce from minibar items list
    const targetMinibar = state.minibars.find(m => m.id === minibarId);
    if (!targetMinibar) return;

    const updatedContainedItems = targetMinibar.items
      .map(it => {
        if (it.itemId === itemId) {
          return { ...it, quantity: it.quantity - qty };
        }
        return it;
      })
      .filter(it => it.quantity > 0);

    const updatedMinibar = { ...targetMinibar, items: updatedContainedItems };

    const ok1 = await dbSetDoc('stockItems', itemId, updatedStockItem);
    const ok2 = await dbSetDoc('minibars', minibarId, updatedMinibar);

    if (ok1 && ok2) {
      const updatedStockItems = state.stockItems.map(si => si.id === itemId ? updatedStockItem : si);
      const updatedMinibars = state.minibars.map(mb => mb.id === minibarId ? updatedMinibar : mb);
      setState(prev => ({
        ...prev,
        stockItems: updatedStockItems,
        minibars: updatedMinibars
      }));
      alert("Insumo devolvido ao estoque com sucesso!");
    } else {
      alert("Erro ao devolver insumo na nuvem.");
    }
  };

  // Consuming item directly from minibar inside stay registers
  const handleUpdateMinibarItemsDirectly = async (minibarId: string, itemId: string, change: number) => {
    const targetMinibar = state.minibars.find(m => m.id === minibarId);
    if (!targetMinibar) return;

    const updatedContainedItems = targetMinibar.items
      .map(it => {
        if (it.itemId === itemId) {
          return { ...it, quantity: it.quantity + change };
        }
        return it;
      })
      .filter(it => it.quantity > 0);

    const updatedMinibar = { ...targetMinibar, items: updatedContainedItems };
    const ok1 = await dbSetDoc('minibars', minibarId, updatedMinibar);

    let updatedStockItems = [...state.stockItems];
    let ok2 = true;
    if (change < 0) {
      const stockItemIdx = state.stockItems.findIndex(i => i.id === itemId);
      if (stockItemIdx !== -1) {
        const stockItem = state.stockItems[stockItemIdx];
        const newQty = Math.max(0, stockItem.quantity + change);
        const updatedStockItem = { ...stockItem, quantity: newQty };
        ok2 = await dbSetDoc('stockItems', itemId, updatedStockItem);
        updatedStockItems[stockItemIdx] = updatedStockItem;
      }
    }

    if (ok1 && ok2) {
      setState(prev => ({
        ...prev,
        minibars: prev.minibars.map(mb => mb.id === minibarId ? updatedMinibar : mb),
        stockItems: updatedStockItems
      }));
    } else {
      alert("Erro ao atualizar frigobar na nuvem.");
    }
  };

  // RESERVATIONS
  const handleAddReservation = async (res: Reservation) => {
    const resWithHotel = {
      ...res,
      hotelId: state.selectedHotelId
    };
    const ok = await dbSetDoc('reservations', res.id, resWithHotel);
    if (ok) {
      setState(prev => ({
        ...prev,
        reservations: [resWithHotel, ...prev.reservations]
      }));
      alert(`Reserva ${res.code} agendada operativamente como Pendente.`);
    } else {
      alert("Erro ao salvar reserva na nuvem.");
    }
  };

  const handleUpdateReservation = async (id: string, updates: Partial<Reservation>) => {
    const target = state.reservations.find(r => r.id === id);
    if (target) {
      const updated = { ...target, ...updates, updatedAt: new Date().toISOString(), updatedBy: state.activeRole };
      const ok = await dbSetDoc('reservations', id, updated);
      if (ok) {
        setState(prev => ({
          ...prev,
          reservations: prev.reservations.map(r => r.id === id ? updated : r)
        }));
      } else {
        alert("Erro ao atualizar reserva na nuvem.");
      }
    }
  };


  // Login Screen Fallback, matching page 4 & 5
  if (!state.isLoggedIn) {
    return (
      <Login 
        onLogin={handleLogin} 
        hotels={state.hotels || []}
      />
    );
  }

  // Active hotel and current profile resolving
  const activeHotel = state.hotels?.find(h => h.id === state.selectedHotelId) || state.hotels?.[0] || {
    id: "hotel_1",
    name: "Pousada Recanto do Sol",
    corporateName: "F. M. Dos Santos Hospedagens LTDA",
    document: "28.190.283/0001-92",
    phone: "(21) 2634-1922",
    email: "contato@pousadarecantodosol.com.br",
    address: "Rua das Conchas, 145 - Centro, Búzios - RJ, CEP: 28950-000",
    pixKey: "contato@pousadarecantodosol.com.br",
    active: true,
  };

  const currentProfile: CompanyProfile = {
    name: activeHotel.name,
    corporateName: activeHotel.corporateName,
    document: activeHotel.document,
    phone: activeHotel.phone,
    email: activeHotel.email,
    address: activeHotel.address,
    pixKey: activeHotel.pixKey,
  };

  // Filtered array subsets for current active hotel
  const hotelReservations = state.reservations.filter(r => !r.hotelId || r.hotelId === state.selectedHotelId);
  const hotelAccommodations = state.accommodations.filter(a => !a.hotelId || a.hotelId === state.selectedHotelId);
  const hotelStockItems = state.stockItems.filter(s => !s.hotelId || s.hotelId === state.selectedHotelId);
  const hotelMinibars = state.minibars.filter(m => !m.hotelId || m.hotelId === state.selectedHotelId);
  const hotelParkingSpots = state.parkingSpots.filter(p => !p.hotelId || p.hotelId === state.selectedHotelId);
  const hotelClients = state.clients.filter(c => !c.hotelId || c.hotelId === state.selectedHotelId || isSuperAdmin);

  // Sidebar Links config (filters role authorizations)
  const sidebarLinks = [
    { id: 'reservas', label: 'Reservas & Estadias', icon: Calendar },
    { id: 'mapa', label: 'Mapa de Acomodações', icon: Bed },
    { id: 'acomodacoes', label: 'Acomodações', icon: Home },
    { id: 'clientes', label: 'Hóspedes', icon: Users },
    { id: 'frigobar', label: 'Frigobares', icon: Compass },
    { id: 'estoque', label: 'Estoque Central', icon: Package },
    { id: 'estacionamento', label: 'Estacionamento', icon: Layers },
    { id: 'relatorios', label: 'Relatórios do Painel', icon: TrendingUp },
    { id: 'hoteis', label: 'Gerenciar Hotéis', icon: Building2, superOnly: true },
    { id: 'config', label: 'Dados da Unidade', icon: Settings, adminOnly: true },
  ].filter(link => {
    if (isSuperAdmin) {
      return link.id === 'hoteis';
    }
    if (link.superOnly) return isSuperAdmin;
    if (link.adminOnly) return isAdmin; // isAdmin is true for 'administrador' and 'super'
    return true;
  });

  return (
    <div className="min-h-screen bg-zinc-50 antialiased font-sans text-zinc-800">
      {/* Mobile Header - Only visible on mobile */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-zinc-900 text-white border-b border-zinc-800 safe-top">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <img src={skyHostLogo} alt="SkyHost" className="w-7 h-7 object-contain mix-blend-lighten shrink-0" />
            {isSuperAdmin ? (
              <select
                value={state.selectedHotelId}
                onChange={(e) => {
                  const hId = e.target.value;
                  setState(prev => ({ ...prev, selectedHotelId: hId }));
                }}
                id="mobile-hotel-selector"
                className="bg-zinc-800 text-white text-[11px] font-bold rounded px-2 py-1 focus:outline-none border border-zinc-700 truncate cursor-pointer min-w-0 max-w-[180px]"
              >
                {state.hotels?.map(h => (
                  <option key={h.id} value={h.id} className="bg-zinc-900 text-white text-xs">
                    {h.name} {!h.active ? ' (Inativa)' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-white text-xs font-bold px-2 py-1 truncate bg-zinc-800 rounded max-w-[180px]">
                {activeHotel.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
              isSuperAdmin ? 'bg-purple-900/50 text-purple-300' :
              state.activeRole === 'administrador' ? 'bg-green-900/50 text-green-300' :
              'bg-blue-900/50 text-blue-300'
            }`}>
              {state.activeRole === 'super' ? 'SUPER' : state.activeRole === 'administrador' ? 'ADM' : 'RECEP'}
            </span>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 border border-zinc-700 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer transition-colors"
              aria-label="Abrir menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Spacer */}
      <div className="md:hidden h-[52px]"></div>

      {/* Mobile Navigation Drawer */}
      <div className={`nav-drawer ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="nav-drawer-backdrop" onClick={() => setMobileMenuOpen(false)}></div>
        <div className="nav-drawer-content">
          {/* Drawer Header */}
          <div className="p-4 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between">
            <img src={skyHostLogo} alt="SkyHost" className="w-32 h-auto object-contain mix-blend-lighten" />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 border border-zinc-700 rounded-lg text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer"
              aria-label="Fechar menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Hotel Selector in Drawer */}
          {isSuperAdmin && (
            <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-950/20">
              <label className="block text-[9px] font-extrabold text-zinc-500 uppercase tracking-widest mb-1.5">
                Escolher Unidade
              </label>
              <select
                value={state.selectedHotelId}
                onChange={(e) => {
                  const hId = e.target.value;
                  setState(prev => ({ ...prev, selectedHotelId: hId }));
                }}
                className="w-full bg-zinc-800 hover:bg-zinc-750 text-white text-xs font-semibold rounded-lg px-2.5 py-2 focus:outline-none border border-zinc-750 cursor-pointer"
              >
                {state.hotels?.map(h => (
                  <option key={h.id} value={h.id} className="bg-zinc-900">
                    {h.name} {!h.active ? ' (Inativa)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Access Level Indicator */}
          <div className="px-4 py-3 bg-zinc-950/40 border-b border-zinc-800">
            <span className="text-[9px] font-extrabold text-zinc-400 uppercase tracking-widest">Nível de Acesso</span>
            <div className="mt-1">
              <span className={`text-[11px] font-bold uppercase ${isSuperAdmin ? 'text-purple-400' : state.activeRole === 'administrador' ? 'text-green-500' : 'text-blue-400'}`}>
                ● {state.activeRole === 'super' ? 'SUPER ADMIN' : state.activeRole}
              </span>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {sidebarLinks.map(link => {
              const Icon = link.icon;
              const isSelected = activeTab === link.id;
              return (
                <button
                  key={link.id}
                  id={`mobile-sidebar-link-${link.id}`}
                  onClick={() => {
                    setActiveTab(link.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-3 text-sm rounded-lg transition-all cursor-pointer font-semibold ${
                    isSelected
                      ? 'bg-yellow-500 text-zinc-950'
                      : 'text-zinc-300 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className={`w-5 h-5 shrink-0 ${isSelected ? 'text-zinc-950' : 'text-zinc-500'}`} />
                  <span>{link.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-zinc-800 bg-zinc-950/20 safe-bottom">
            <button
              type="button"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 hover:bg-zinc-800 text-zinc-300 hover:text-red-400 rounded-lg text-xs font-extrabold uppercase tracking-wider cursor-pointer border border-zinc-800 transition-all"
            >
              <LogOut className="w-4 h-4" /> Sair do Sistema
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Desktop Sidebar - Hidden on mobile */}
        <aside className="hidden md:flex w-64 lg:w-68 bg-zinc-900 text-white flex-col border-r border-zinc-800/60 shrink-0 sticky top-0 h-screen">
          {/* Core Profile Area */}
          <div className="p-4 lg:p-5 border-b border-zinc-800 flex flex-col items-center justify-center text-center bg-zinc-950">
            <div className="w-full max-w-[160px] shrink-0 flex items-center justify-center">
              <img src={skyHostLogo} alt="SkyHost" className="w-full h-auto object-contain mix-blend-lighten" />
            </div>
          </div>

          {/* Hotel Dropdown Selector in Desktop Sidebar */}
          {isSuperAdmin ? (
            <div className="px-4 lg:px-5 py-3 border-b border-zinc-800 bg-zinc-950/20">
              <label className="block text-[9px] lg:text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest mb-1.5">
                Escolher Unidade
              </label>
              <select
                value={state.selectedHotelId}
                onChange={(e) => {
                  const hId = e.target.value;
                  setState(prev => ({ ...prev, selectedHotelId: hId }));
                }}
                id="desktop-hotel-selector"
                className="w-full bg-zinc-800 hover:bg-zinc-750 text-white text-xs font-semibold rounded-lg px-2.5 py-2 focus:outline-none border border-zinc-750 cursor-pointer transition-colors"
              >
                {state.hotels?.map(h => (
                  <option key={h.id} value={h.id} className="bg-zinc-900">
                    {h.name} {!h.active ? ' (Inativa)' : ''}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="px-4 lg:px-5 py-3 border-b border-zinc-800 bg-zinc-950/10">
              <label className="block text-[9px] lg:text-[10px] font-extrabold text-zinc-400 uppercase tracking-widest mb-1">
                Unidade Ativa
              </label>
              <span className="text-zinc-100 text-xs font-extrabold block truncate">
                {activeHotel.name}
              </span>
            </div>
          )}

          {/* Current Active Operator Profile */}
          <div className="px-4 lg:px-5 py-3 bg-zinc-950/40 border-b border-zinc-800">
            <span className="text-[9px] lg:text-[10px] font-extrabold text-zinc-400 uppercase tracking-widest">Nível de Acesso</span>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] lg:text-[11px] font-bold uppercase ${isSuperAdmin ? 'text-purple-400' : state.activeRole === 'administrador' ? 'text-green-500' : 'text-blue-400'}`}>
                ● {state.activeRole === 'super' ? 'SUPER ADMIN' : state.activeRole}
              </span>
            </div>
          </div>

          {/* Links stack */}
          <nav className="flex-1 p-3 lg:p-4 space-y-1 overflow-y-auto">
            {sidebarLinks.map(link => {
              const Icon = link.icon;
              const isSelected = activeTab === link.id;
              return (
                <button
                  key={link.id}
                  id={`sidebar-link-${link.id}`}
                  onClick={() => {
                    setActiveTab(link.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-xs rounded-lg transition-all cursor-pointer font-semibold ${
                    isSelected
                      ? 'bg-yellow-500 text-zinc-950'
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isSelected ? 'text-zinc-950' : 'text-zinc-500'}`} />
                  <span className="truncate">{link.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout area */}
          <div className="p-3 lg:p-4 border-t border-zinc-800 bg-zinc-950/20">
            <button
              type="button"
              id="btn-sidebar-logout"
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 hover:bg-zinc-800 text-zinc-400 hover:text-red-400 rounded-lg text-[10px] lg:text-xxs font-extrabold uppercase tracking-wider cursor-pointer border border-zinc-800/80 transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Sair do Sistema
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 main-content min-w-0">
          {/* Page Header with context */}
          <div className="md:hidden mb-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Operador</p>
              <p className="text-xs font-semibold text-zinc-800 truncate">{activeHotel.name}</p>
            </div>
          </div>
        {/* Render Active View Selector */}
        {activeTab === 'reservas' && (
          <Reservas
            reservations={hotelReservations}
            clients={hotelClients}
            accommodations={hotelAccommodations}
            minibars={hotelMinibars}
            stockItems={hotelStockItems}
            profile={currentProfile}
            activeRole={state.activeRole}
            onAddReservation={handleAddReservation}
            onUpdateReservation={handleUpdateReservation}
            onUpdateMinibarItems={handleUpdateMinibarItemsDirectly}
          />
        )}

        {activeTab === 'mapa' && (
          <AccommodationBoard
            accommodations={hotelAccommodations}
            reservations={hotelReservations}
          />
        )}

        {activeTab === 'acomodacoes' && (
          <Accommodations
            accommodations={hotelAccommodations}
            accommodationTypes={state.accommodationTypes}
            parkingSpots={hotelParkingSpots}
            onAddAccommodationType={handleAddAccType}
            onUpdateAccommodationType={handleUpdateAccType}
            onAddAccommodation={handleAddAccommodation}
            onUpdateAccommodation={handleUpdateAccommodation}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'clientes' && (
          <Clients
            clients={hotelClients}
            onAddClient={handleAddClient}
            onUpdateClient={async (id, updates) => {
              const ok = await dbSetDoc('clients', id, { ...state.clients.find(c => c.id === id), ...updates });
              if (ok) {
                setState(prev => ({
                  ...prev,
                  clients: prev.clients.map(c => c.id === id ? { ...c, ...updates } : c)
                }));
              } else {
                alert("Erro ao atualizar hóspede na nuvem.");
              }
            }}
            profile={currentProfile}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'frigobar' && (
          <Minibars
            minibars={hotelMinibars}
            accommodations={hotelAccommodations}
            stockItems={hotelStockItems}
            stockCategories={state.stockCategories}
            onAddMinibar={handleAddMinibar}
            onUpdateMinibar={handleUpdateMinibar}
            onAddItemToMinibar={handleAddItemToMinibar}
            onRemoveItemFromMinibar={handleRemoveItemFromMinibar}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'estoque' && (
          <Stock
            stockItems={hotelStockItems}
            stockCategories={state.stockCategories}
            onAddStockItem={handleAddStockItem}
            onUpdateStockItem={handleUpdateStockItem}
            onAddStockItemBulk={handleAddStockItemBulk}
            onAddCategory={handleAddStockCategory}
            onUpdateCategory={handleUpdateStockCategory}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'estacionamento' && (
          <Parking
            parkingSpots={hotelParkingSpots}
            onAddParkingSpot={handleAddParkingSpot}
            onDeleteParkingSpot={handleDeleteParkingSpot}
            onUpdateParkingSpot={handleUpdateParkingSpot}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'relatorios' && (
          <Reports
            reservations={hotelReservations}
            clients={hotelClients}
            accommodations={hotelAccommodations}
            accommodationTypes={state.accommodationTypes}
            stockItems={hotelStockItems}
            stockCategories={state.stockCategories}
            minibars={hotelMinibars}
            profile={currentProfile}
          />
        )}

        {activeTab === 'hoteis' && isSuperAdmin && (
          <Hotels
            hotels={state.hotels || []}
            selectedHotelId={state.selectedHotelId}
            onAddHotel={handleAddHotel}
            onUpdateHotel={handleUpdateHotel}
            onSelectHotel={(hId) => setState(prev => ({ ...prev, selectedHotelId: hId }))}
            isAdmin={isAdmin}
          />
        )}

        {activeTab === 'config' && isAdmin && (
          <ConfigProfile
            profile={currentProfile}
            onSave={handleUpdateProfile}
            isAdmin={isAdmin}
          />
        )}
        </main>
      </div>
    </div>
  );
}
